import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaPaperPlane, FaTimes } from "react-icons/fa";
import EmailRichTextEditor, {
  isEmptyEmailHtml,
} from "../../GenericComponents/EmailRichTextEditor";
import { getUser } from "../../../utilis/Common";
import {
  fetchEmailTags,
  previewProviderEmailSend,
  sendProviderEmail,
} from "./inquiryBatchApi";
import { formatBatchDateTime } from "./inquiryBatchUtils";
import { canSendBatchItemToProvider, isResendBatchItemToProvider } from "./inquiryApplicationUtils";

const inputClass =
  "h-9 w-full rounded border border-[#eef0ff] bg-[#f8f9ff] px-2.5 text-[13px] font-medium text-slate-800 outline-none transition focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]/20";

function providerKey(providerId) {
  return String(providerId ?? 0);
}

function isValidEmail(value = "") {
  const text = String(value || "").trim();
  if (!text) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text);
}

function normalizeEmailAddress(value = "") {
  return String(value || "").trim().toLowerCase();
}

function parseEmailTokens(value = "") {
  return String(value || "")
    .split(/[,;\s\n]+/)
    .map((email) => email.trim())
    .filter(Boolean);
}

function getProviderEmailHint(group = {}) {
  if (!group?.has_saved_email) {
    return "No Accounts Manager email on file. Enter the provider email address to send.";
  }

  const designation = String(group.recipient_designation || "").trim();
  const contactName = String(group.recipient_contact_name || "").trim();

  if (designation.toLowerCase() === "accounts manager") {
    return contactName
      ? `Loaded from Accounts Manager contact (${contactName}).`
      : "Loaded from Accounts Manager contact.";
  }

  if (designation) {
    return contactName
      ? `Loaded from ${designation} contact (${contactName}).`
      : `Loaded from ${designation} contact.`;
  }

  return contactName
    ? `Loaded from provider contact (${contactName}).`
    : "Loaded from provider contact.";
}

function CcEmailsField({ emails = [], onChange, disabled = false }) {
  const [inputValue, setInputValue] = useState("");
  const [inputError, setInputError] = useState("");

  const addEmails = (rawValue) => {
    const tokens = parseEmailTokens(rawValue);
    if (!tokens.length) return;

    const invalid = tokens.find((email) => !isValidEmail(email));
    if (invalid) {
      setInputError(`"${invalid}" is not a valid email address.`);
      return;
    }

    const existing = new Set(emails.map(normalizeEmailAddress));
    const next = [...emails];

    tokens.forEach((email) => {
      const normalized = normalizeEmailAddress(email);
      if (!existing.has(normalized)) {
        existing.add(normalized);
        next.push(email.trim());
      }
    });

    onChange(next);
    setInputValue("");
    setInputError("");
  };

  const commitInputEmail = () => {
    if (!inputValue.trim()) return false;
    addEmails(inputValue);
    return true;
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === "," || event.key === " ") {
      event.preventDefault();
      commitInputEmail();
      return;
    }

    if (event.key === "Tab" && inputValue.trim()) {
      event.preventDefault();
      commitInputEmail();
      return;
    }

    if (event.key === "Backspace" && !inputValue && emails.length) {
      onChange(emails.slice(0, -1));
    }
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmails(inputValue);
    }
  };

  const removeEmail = (index) => {
    onChange(emails.filter((_, itemIndex) => itemIndex !== index));
    setInputError("");
  };

  return (
    <label className="block max-w-md">
      <span className="mb-1 block text-[12px] font-semibold text-slate-700">
        CC
      </span>

      <div
        className={`min-h-9 rounded border bg-[#f8f9ff] px-2 py-1.5 transition focus-within:border-[#007BFF] focus-within:ring-1 focus-within:ring-[#007BFF]/20 ${
          inputError
            ? "border-red-300 focus-within:border-red-500 focus-within:ring-red-500/20"
            : "border-[#eef0ff]"
        }`}
      >
        <div className="flex flex-wrap items-center gap-1.5">
          {emails.map((email, index) => (
            <span
              key={`${email}-${index}`}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 ring-1 ring-slate-200"
            >
              <span className="truncate">{email}</span>
              <button
                type="button"
                onClick={() => removeEmail(index)}
                disabled={disabled}
                className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50"
                aria-label={`Remove ${email}`}
              >
                <FaTimes size={9} />
              </button>
            </span>
          ))}

          <input
            type="text"
            value={inputValue}
            onChange={(event) => {
              setInputValue(event.target.value);
              if (inputError) setInputError("");
            }}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={emails.length ? "Add another email" : "Enter CC emails"}
            className="min-w-[140px] flex-1 border-0 bg-transparent px-1 py-0.5 text-[13px] font-medium text-slate-800 outline-none"
          />
        </div>
      </div>

      {inputError ? (
        <p className="mb-0 mt-1 text-[11px] font-medium text-red-600">
          {inputError}
        </p>
      ) : (
        <p className="mb-0 mt-1 text-[11px] text-slate-500">
          Optional. Add multiple emails separated by comma, space, or Enter.
        </p>
      )}
    </label>
  );
}

function getBranchPhone(branch = "") {
  const key = String(branch || "")
    .trim()
    .toLowerCase();
  if (key === "lahore") return "0092 344 000 2829";
  if (key === "sydney") return "0061 2 8212 4505 | 0432 710 739";
  if (key === "samundri") return "0092 334 4666 480";
  if (key === "sahiwal") return "0092 300 696 3438";
  if (key === "faislabad") return "0092 305 184 4480";
  return "";
}

function defaultSubject(batch = {}) {
  return (
    String(
      batch?.subject || batch?.batch_name || batch?.batch_no || "",
    ).trim() || "Commission inquiry"
  );
}

function escapeHtml(text = "") {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildEmailSignatureHtml(userName, branchLabel, branchPhone) {
  const branchLine = branchLabel
    ? `<p>Aptitude Migration (${escapeHtml(branchLabel)} Office)</p>`
    : "<p>Aptitude Migration</p>";
  const phoneLine = branchPhone
    ? `<p>${escapeHtml(branchPhone)}</p>`
    : "<p><br></p>";

  return [
    "<p>Thank you</p>",
    `<p><strong>${escapeHtml(userName || "Team member")}</strong></p>`,
    branchLine,
    phoneLine,
  ].join("");
}

function buildDefaultEmailBody({
  providerName = "Provider",
  userName = "Team member",
  branchLabel = "",
  branchPhone = "",
  middleHtml = "",
}) {
  const greeting = `<p>Hi ${escapeHtml(providerName)},</p>`;
  const middle =
    middleHtml && !isEmptyEmailHtml(middleHtml)
      ? middleHtml
      : "<p><br></p><p><br></p>";

  return `${greeting}${middle}${buildEmailSignatureHtml(
    userName,
    branchLabel,
    branchPhone,
  )}`;
}

function buildEmailBodyFromTag({
  providerName,
  tagHtml,
  userName,
  branchLabel,
  branchPhone,
}) {
  return buildDefaultEmailBody({
    providerName,
    userName,
    branchLabel,
    branchPhone,
    middleHtml: tagHtml,
  });
}

function InquiryBatchSendProviderModal({
  open,
  batchId,
  batch,
  items = [],
  onClose,
  onSent,
}) {
  const loggedInUser = useMemo(() => getUser() || {}, []);
  const [subject, setSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [recipientEmails, setRecipientEmails] = useState({});
  const [emailTags, setEmailTags] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [targetItems, setTargetItems] = useState([]);
  const targetIdsRef = useRef([]);
  const [ccEmails, setCcEmails] = useState([]);

  const itemIds = useMemo(
    () => targetItems.map((item) => Number(item.id)).filter((id) => id > 0),
    [targetItems],
  );

  const groups = Array.isArray(preview?.groups) ? preview.groups : [];
  const primaryProviderName =
    groups[0]?.provider_name || targetItems[0]?.provider_name || "Provider";
  const resendCount = targetItems.filter(isResendBatchItemToProvider).length;
  const isResendOnly =
    resendCount > 0 && resendCount === targetItems.length;
  const branchLabel =
    loggedInUser?.branch_name ||
    loggedInUser?.branch ||
    loggedInUser?.agencyname ||
    "";
  const branchPhone = getBranchPhone(branchLabel);

  useEffect(() => {
    if (!open) {
      setTargetItems([]);
      targetIdsRef.current = [];
      setPreview(null);
      setSubject("");
      setEmailBody("");
      setRecipientEmails({});
      setCcEmails([]);
      setEmailTags([]);
      setError("");
      return undefined;
    }

    if (targetIdsRef.current.length > 0) return undefined;

    const snapshot = (Array.isArray(items) ? items : []).filter(
      (item) => item?.id != null && canSendBatchItemToProvider(item, batch),
    );
    if (snapshot.length === 0) return undefined;

    setTargetItems(snapshot);
    targetIdsRef.current = snapshot.map((item) => Number(item.id));
    setSubject(defaultSubject(batch));
    setEmailBody(
      buildDefaultEmailBody({
        providerName: snapshot[0]?.provider_name || "Provider",
        userName: loggedInUser?.name,
        branchLabel,
        branchPhone,
      }),
    );
    setRecipientEmails({});
    setCcEmails([]);
    setError("");
    setPreview(null);

    fetchEmailTags("email")
      .then((tags) => setEmailTags(Array.isArray(tags) ? tags : []))
      .catch(() => setEmailTags([]));

    return undefined;
  }, [open, batchId, batch, items]);

  useEffect(() => {
    if (!open || !batchId || itemIds.length === 0) return undefined;

    let cancelled = false;
    setLoadingPreview(true);
    setError("");

    previewProviderEmailSend(batchId, itemIds)
      .then((data) => {
        if (cancelled) return;
        setPreview(data);

        const providerName =
          data?.groups?.[0]?.provider_name ||
          targetItems[0]?.provider_name ||
          "Provider";

        setEmailBody((current) =>
          !current || isEmptyEmailHtml(current)
            ? buildDefaultEmailBody({
                providerName,
                userName: loggedInUser?.name,
                branchLabel,
                branchPhone,
              })
            : current,
        );

        const nextEmails = {};
        (data?.groups || []).forEach((group) => {
          nextEmails[providerKey(group.provider_id)] =
            group.recipient_email || "";
        });
        setRecipientEmails(nextEmails);
      })
      .catch((err) => {
        if (cancelled) return;
        setPreview(null);
        setError(err?.message || "Failed to load provider details.");
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, batchId, itemIds]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !submitting) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, submitting]);

  const allRecipientEmailsValid = useMemo(
    () =>
      groups.length > 0 &&
      groups.every((group) =>
        isValidEmail(recipientEmails[providerKey(group.provider_id)]),
      ),
    [groups, recipientEmails],
  );

  const isCcEmailValid = useMemo(
    () => ccEmails.every((email) => isValidEmail(email)),
    [ccEmails],
  );

  const canSend =
    !loadingPreview &&
    !submitting &&
    groups.length > 0 &&
    String(subject || "").trim().length > 0 &&
    allRecipientEmailsValid &&
    isCcEmailValid &&
    !isEmptyEmailHtml(emailBody);

  if (!open || targetItems.length === 0) return null;

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  const updateRecipientEmail = (groupProviderId, value) => {
    setRecipientEmails((prev) => ({
      ...prev,
      [providerKey(groupProviderId)]: value,
    }));
  };

  const handleTagSelect = (event) => {
    const value = event.target.value;
    if (!value) return;
    setEmailBody(
      buildEmailBodyFromTag({
        providerName: primaryProviderName,
        tagHtml: value,
        userName: loggedInUser?.name,
        branchLabel,
        branchPhone,
      }),
    );
    event.target.value = "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSend) return;

    setError("");
    setSubmitting(true);
    try {
      const result = await sendProviderEmail(batchId, {
        batch_item_ids: targetIdsRef.current.length
          ? targetIdsRef.current
          : itemIds,
        subject: subject.trim(),
        email_body: emailBody,
        cc_emails: ccEmails,
        recipient_emails: groups.map((group) => ({
          provider_id: group.provider_id ?? 0,
          recipient_email: String(
            recipientEmails[providerKey(group.provider_id)] || "",
          ).trim(),
        })),
      });
      onSent?.(result);
    } catch (err) {
      setError(err?.message || "Failed to send provider email.");
    } finally {
      setSubmitting(false);
    }
  };

  return createPortal(
    <>
      <div
        className="cms-app-modal-backdrop bg-black/40"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div
        className="cms-app-modal cms-app-modal--centered flex items-center justify-center p-2 sm:p-3"
        role="dialog"
        aria-modal="true"
        aria-labelledby="inquiry-send-provider-title"
        onClick={handleClose}
      >
        <div
          className="tailwind-container relative z-10 flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col"
          >
            <div className="flex items-start justify-between gap-2 border-b border-slate-100 px-4 py-3">
              <div className="min-w-0">
                <h3
                  id="inquiry-send-provider-title"
                  className="mb-0 text-[15px] font-semibold text-slate-900"
                >
                  {isResendOnly ? "Resend email to provider" : "Send email to provider"}
                </h3>
                <p className="mb-0 text-[12px] text-slate-500">
                  {loadingPreview
                    ? "Loading provider details…"
                    : `${targetItems.length} student${targetItems.length === 1 ? "" : "s"}${resendCount > 0 ? ` (${resendCount} resend${resendCount === 1 ? "" : "s"})` : ""} · Excel attachment included`}
                </p>
              </div>
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-50"
                style={{ boxShadow: "none" }}
                aria-label="Close"
              >
                <FaTimes size={12} />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="block min-w-0">
                  <span className="mb-1 block text-[12px] font-semibold text-slate-700">
                    Subject
                  </span>
                  <input
                    type="text"
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    disabled={submitting || loadingPreview}
                    placeholder="Please enter"
                    className={inputClass}
                  />
                </label>

                {groups.length === 1 ? (
                  <label className="block min-w-0">
                    <span className="mb-1 block text-[12px] font-semibold text-slate-700">
                      Provider Email
                    </span>
                    <input
                      type="email"
                      value={
                        recipientEmails[providerKey(groups[0].provider_id)] ??
                        ""
                      }
                      onChange={(event) =>
                        updateRecipientEmail(
                          groups[0].provider_id,
                          event.target.value,
                        )
                      }
                      disabled={submitting || loadingPreview}
                      placeholder="Please enter"
                      className={inputClass}
                    />
                    <p className="mb-0 mt-1 text-[11px] text-slate-500">
                      {getProviderEmailHint(groups[0])}
                    </p>
                  </label>
                ) : (
                  <div className="rounded-md border border-slate-200 bg-slate-50/70 px-2.5 py-2">
                    <p className="mb-1 text-[12px] font-semibold text-slate-700">
                      Providers in this send
                    </p>
                    <p className="mb-0 text-[11px] text-slate-500">
                      Enter an email for each provider below.
                    </p>
                  </div>
                )}
              </div>

              <CcEmailsField
                emails={ccEmails}
                onChange={setCcEmails}
                disabled={submitting || loadingPreview}
              />

              <label className="block max-w-md">
                <span className="mb-1 block text-[12px] font-semibold text-slate-700">
                  Description Tags
                </span>
                <select
                  className={`${inputClass} h-10`}
                  defaultValue=""
                  onChange={handleTagSelect}
                  disabled={submitting || loadingPreview}
                >
                  <option value="">Please Select</option>
                  {[...emailTags]
                    .sort((a, b) =>
                      String(a.name).localeCompare(String(b.name)),
                    )
                    .map((tag) => (
                      <option key={tag.id} value={tag.description}>
                        {tag.name}
                      </option>
                    ))}
                </select>
              </label>

              {groups.length > 1 ? (
                groups.map((group) => {
                  const key = providerKey(group.provider_id);
                  const emailValue = recipientEmails[key] ?? "";
                  const emailInvalid =
                    emailValue.trim() !== "" && !isValidEmail(emailValue);

                  return (
                    <div
                      key={`${group.provider_id || "unknown"}-${group.provider_name}`}
                      className="rounded-md border border-slate-200 bg-slate-50/80 p-2.5"
                    >
                      <p className="mb-1 text-[12px] font-semibold text-slate-800">
                        {group.provider_name || "Provider"}
                      </p>
                      <input
                        type="email"
                        value={emailValue}
                        onChange={(event) =>
                          updateRecipientEmail(
                            group.provider_id,
                            event.target.value,
                          )
                        }
                        disabled={submitting || loadingPreview}
                        placeholder="Provider email"
                        className={`${inputClass} mb-2 ${emailInvalid ? "border-red-300" : ""}`}
                      />
                      <div className="flex flex-wrap gap-1">
                        {(group.students || []).map((student) => (
                          <span
                            key={student.batch_item_id}
                            className="inline-flex rounded-full bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200"
                          >
                            {student.student_name || "Student"}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })
              ) : groups[0] ? (
                <div className="flex flex-wrap gap-1">
                  {(groups[0].students || []).map((student) => (
                    <span
                      key={student.batch_item_id}
                      className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600 ring-1 ring-slate-200"
                    >
                      {student.student_name || "Student"}
                    </span>
                  ))}
                </div>
              ) : null}

              <div>
                <span className="mb-1 block text-[12px] font-semibold text-slate-700">
                  Email message
                </span>
                <EmailRichTextEditor
                  value={emailBody}
                  onChange={setEmailBody}
                  placeholder="Please Enter"
                />
              </div>

              {error ? (
                <p className="mb-0 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[12px] font-medium text-rose-700">
                  {error}
                </p>
              ) : null}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-4 py-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                className="inline-flex h-9 items-center rounded-md bg-red-600 px-4 text-[12px] font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                style={{ border: "none", boxShadow: "none" }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSend}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-emerald-600 px-4 text-[12px] font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ border: "none", boxShadow: "none" }}
              >
                <FaPaperPlane size={11} />
                {submitting
                  ? "Sending…"
                  : isResendOnly
                    ? "Resend email"
                    : "Send email"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>,
    document.body,
  );
}

export function InquiryBatchProviderEmailHistory({ emails = [] }) {
  const list = Array.isArray(emails) ? emails : [];
  if (!list.length) return null;

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-white p-3 shadow-sm">
      <h2 className="mb-2 text-[13px] font-semibold text-slate-800">
        Provider emails sent
      </h2>
      <div className="space-y-2">
        {list.map((email, index) => {
          const sendNumber = list.length - index;
          return (
          <div
            key={email.id}
            className="rounded-md border border-slate-100 bg-slate-50/60 px-2.5 py-2"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="mb-0 text-[12px] font-semibold text-slate-800">
                  <span className="mr-1.5 rounded bg-slate-200/80 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                    Send #{sendNumber}
                  </span>
                  {email.subject || email.provider_name || "Provider"}
                </p>
                <p className="mb-0 text-[11px] text-slate-500">
                  To: {email.recipient_email || "—"}
                  {Array.isArray(email.cc_emails) && email.cc_emails.length > 0
                    ? ` · CC: ${email.cc_emails.join(", ")}`
                    : ""}
                  {" · "}
                  {formatBatchDateTime(email.sent_at)}
                  {email.sent_by_name ? ` · ${email.sent_by_name}` : ""}
                  {email.attachment_name ? ` · ${email.attachment_name}` : ""}
                </p>
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${
                  email.email_delivered
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : "bg-amber-50 text-amber-700 ring-amber-200"
                }`}
              >
                {email.email_delivered ? "Sent" : "Not sent"}
              </span>
            </div>
            <div
              className="prose prose-sm mb-0 mt-1.5 max-w-none text-[12px] text-slate-700"
              dangerouslySetInnerHTML={{
                __html: email.email_body || "<p>—</p>",
              }}
            />
          </div>
          );
        })}
      </div>
    </div>
  );
}

export default InquiryBatchSendProviderModal;
